import { ENV } from "@/config/env";
import { companyRequest } from "@/services/companyRequest";
import sessionManager from "@/services/sessionManager";
import { createUuid } from "@/utils/uuid";
import { store } from "@redux/store";
import authActions from "@redux/auth/actions";
import { readErrorDetail } from "./error-response.mjs";
import {
  createStreamCompletionTracker,
  dispatchStreamEvent,
  parseSseBlock,
} from "./streaming.mjs";

const aiBasePath = (companyId, facilityId) =>
  `/api/v2/facilities/${encodeURIComponent(companyId)}/${encodeURIComponent(facilityId)}/ai`;

const REASONING_EFFORTS = new Set(["none", "low", "medium", "high", "xhigh", "max"]);

const unwrap = (response) => response?.data ?? response;

const createResponseError = async (response, fallbackMessage) => {
  const detail = await readErrorDetail(response);
  const error = new Error(detail || fallbackMessage);
  error.status = response.status;
  return error;
};

const refreshAccessToken = async (refresh, signal) => {
  const response = await fetch(`${ENV.backendUrl}/auth/token/refresh`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ refresh }),
    signal,
  });

  if (!response.ok) {
    throw await createResponseError(
      response,
      `Token refresh failed with status ${response.status}`
    );
  }

  const tokens = await response.json();
  if (!tokens?.access) {
    throw new Error("Token refresh response did not include an access token");
  }

  const session = sessionManager.getSessionSnapshot();
  store.dispatch(authActions.updateToken(
    tokens.access,
    tokens.refresh || refresh,
    tokens.access_token_expiration || tokens.accessTokenExpiration || session.accessTokenExpiration,
    tokens.refresh_token_expiration || tokens.refreshTokenExpiration || session.refreshTokenExpiration
  ));

  return tokens.access;
};

export const listFacilityAiConversations = async (companyId, facilityId) => {
  const response = await companyRequest.get(`${aiBasePath(companyId, facilityId)}/conversations/`);
  return unwrap(response);
};

export const getFacilityAiConversation = async (companyId, facilityId, conversationId) => {
  const response = await companyRequest.get(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`
  );
  return unwrap(response);
};

export const getFacilityAiConversationMessages = async (companyId, facilityId, conversationId) => {
  const response = await companyRequest.get(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/messages/`
  );
  return unwrap(response);
};

export const updateFacilityAiConversation = async (companyId, facilityId, conversationId, payload) => {
  const response = await companyRequest.patch(
    `${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`,
    payload
  );
  return unwrap(response);
};

export const deleteFacilityAiConversation = async (companyId, facilityId, conversationId) => {
  await companyRequest.delete(`${aiBasePath(companyId, facilityId)}/conversations/${conversationId}/`);
};

export const transcribeFacilityAiAudio = async ({
  companyId,
  facilityId,
  blob,
  language = "",
  signal,
}) => {
  const formData = new FormData();
  formData.append("audio", blob, "voice.wav");
  if (language) {
    formData.append("language", language);
  }
  const response = await companyRequest.post(
    `${aiBasePath(companyId, facilityId)}/transcribe/`,
    formData,
    // companyRequest defaults to application/json, which makes axios serialize
    // FormData into JSON. Override so the browser sends multipart + boundary.
    { signal, headers: { "Content-Type": "multipart/form-data" } }
  );
  return unwrap(response);
};

export const uploadFacilityAiAttachment = async ({
  companyId,
  facilityId,
  conversationId,
  file,
  signal,
}) => {
  const formData = new FormData();
  formData.append("file", file, file.name || "attachment");
  if (conversationId) {
    formData.append("conversation_id", String(conversationId));
  }
  const response = await companyRequest.post(
    `${aiBasePath(companyId, facilityId)}/attachments/`,
    formData,
    // companyRequest defaults to application/json, which makes axios serialize
    // FormData into JSON. Override so the browser sends multipart + boundary.
    { signal, headers: { "Content-Type": "multipart/form-data" } }
  );
  return unwrap(response);
};

export const streamFacilityAiChat = async ({
  companyId,
  facilityId,
  conversationId,
  message,
  webSearch = "off",
  reasoningEffort = "medium",
  pageContext,
  resubmit = false,
  resubmitMessageId,
  signal,
  onEvent,
}) => {
  const session = sessionManager.getSessionSnapshot();
  const traceId = createUuid();
  const payload = {
    message,
    company_id: Number(companyId),
    facility_id: Number(facilityId),
    web_search: webSearch === "auto" ? "auto" : "off",
    llm_reasoning_effort: REASONING_EFFORTS.has(reasoningEffort)
      ? reasoningEffort
      : "medium",
  };

  if (conversationId) {
    payload.conversation_id = conversationId;
  }

  if (pageContext) {
    payload.page_context = pageContext;
  }

  if (resubmit) {
    payload.resubmit = true;
    if (typeof resubmitMessageId === "number") {
      payload.resubmit_message_id = resubmitMessageId;
    }
  }

  const streamUrl = `${ENV.backendUrl}${aiBasePath(companyId, facilityId)}/chat/stream/`;
  const requestStream = (access) => fetch(streamUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: "text/event-stream",
      Authorization: access ? `Bearer ${access}` : "",
      "X-Trace-Id": traceId,
    },
    body: JSON.stringify(payload),
    signal,
  });

  let response = await requestStream(session.access);
  if (response.status === 401) {
    const refresh = sessionManager.getSessionSnapshot().refresh;
    if (!refresh || signal?.aborted) {
      await sessionManager.reset({ reason: "unauthorized" });
      throw await createResponseError(response, "AI request failed with status 401");
    }

    try {
      const access = await refreshAccessToken(refresh, signal);
      response = await requestStream(access);
    } catch (refreshError) {
      if (refreshError?.name !== "AbortError") {
        await sessionManager.reset({ reason: "unauthorized" });
      }
      throw refreshError;
    }
  }

  if (!response.ok || !response.body) {
    throw await createResponseError(
      response,
      `AI request failed with status ${response.status}`
    );
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder("utf-8");
  let buffer = "";
  const completion = createStreamCompletionTracker();

  const deliverEvent = async (event) => {
    await dispatchStreamEvent(event, onEvent);
    completion.observe(event);
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    // Keep the raw buffer until a complete LF/CRLF separator is present so a
    // separator split across ReadableStream chunks cannot drop an event.
    const blocks = buffer.split(/\r?\n\r?\n/);
    buffer = blocks.pop() || "";

    for (const block of blocks) {
      const event = parseSseBlock(block);
      await deliverEvent(event);
    }
  }

  buffer += decoder.decode();
  if (buffer.trim()) {
    const event = parseSseBlock(buffer);
    await deliverEvent(event);
  }
  completion.assertCompleted();
};
